import {
  BUILDING_TILE_KINDS,
  UNIT_KINDS,
  isBuildableUnitKind,
  isTransport,
  unitDef,
  type BuildingTileKind,
  type PlayerId,
  type TileOwner,
  type UnitKind,
} from "@dailywars/core/engine";
import type { ReactNode } from "react";

import { getBuildingImageSrc } from "#/game/svgAssets";
import { getSpriteUnitIconSrc } from "#/game/unitSprites";
import { cn } from "#/lib/utils";

const BUILDING_CAPTURE_LABELS: Record<BuildingTileKind, string> = {
  city: "City",
  barracks: "Barracks",
  factory: "Factory",
  base: "Base",
  airport: "Airport",
  monument: "Monument",
};

export type StatsBreakdownRowKind = "unit" | "building";

export const PROFILE_UNITS_BUILT_KINDS = UNIT_KINDS.filter(isBuildableUnitKind);

export const PROFILE_UNITS_KILLED_KINDS = UNIT_KINDS.filter(
  (kind) => isBuildableUnitKind(kind) && !isTransport(kind),
);

export function unitBreakdownEntries(
  record: Partial<Record<UnitKind, number>>,
  kinds: readonly UnitKind[] = UNIT_KINDS,
): Array<{ key: string; label: string; count: number }> {
  return kinds.map((kind) => ({
    key: kind,
    label: unitDef(kind).name,
    count: record[kind] ?? 0,
  }));
}

export function buildingBreakdownEntries(
  record: Partial<Record<BuildingTileKind, number>>,
): Array<{ key: string; label: string; count: number }> {
  return BUILDING_TILE_KINDS.map((kind) => ({
    key: kind,
    label: BUILDING_CAPTURE_LABELS[kind],
    count: record[kind] ?? 0,
  }));
}

export function StatRowStripe({
  index,
  children,
  compact,
}: {
  index: number;
  children: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between",
        compact ? "py-1.5 px-2" : "py-3 px-3",
        index % 2 === 1 && "bg-muted",
      )}
    >
      {children}
    </div>
  );
}

export function RecordBreakdown({
  entries,
  stripeStartIndex,
  rowKind,
  unitOwner = "p1",
  buildingOwner = "p1",
}: {
  entries: Array<{ key: string; label: string; count: number }>;

  stripeStartIndex: number;
  rowKind: StatsBreakdownRowKind;

  unitOwner?: PlayerId;
  buildingOwner?: TileOwner;
}) {
  return (
    <div className="flex flex-col gap-0 border-t border-border">
      {entries.map((entry, i) => {
        const rowIndex = stripeStartIndex + 1 + i;
        const src =
          rowKind === "unit"
            ? getSpriteUnitIconSrc(entry.key as UnitKind, unitOwner)
            : getBuildingImageSrc(entry.key as BuildingTileKind, buildingOwner);
        return (
          <StatRowStripe key={entry.key} index={rowIndex} compact>
            <span className="flex min-w-0 flex-1 items-center gap-1 pl-2">
              <img src={src} alt="" className="size-8 shrink-0" aria-hidden />
              <span className="text-sm text-muted-foreground">{entry.label}</span>
            </span>
            <span
              className={cn(
                "font-mono text-sm tabular-nums",
                entry.count === 0
                  ? "font-normal text-muted-foreground"
                  : "font-semibold text-foreground",
              )}
            >
              {entry.count.toLocaleString()}
            </span>
          </StatRowStripe>
        );
      })}
    </div>
  );
}
